import {Observable} from 'rxjs';
import {
  WidgetAPI,
  Visibility,
  TriggerParams,
  WidgetHeaderAPI,
  WidgetMenuAPI,
  WidgetNotificationsAPI,
  WidgetVisibilityAPI,
  SoundSetting,
  ViewMode,
} from '.';
import {
  ParentMessageBus,
  requestAnalyticsEventId,
  requestViewMode,
  sendMinimizedHeight,
  sendPreferredSize,
} from '../iframe';
import {Header, Menu} from './header';
import {DefaultNotifications, ParentNotifications} from './notifications';
import {
  DefaultVisibilityController,
  ParentVisibilityController,
} from './visibility';

export const PREFERRED_WIDTH = 360;
export const PREFERRED_HEIGHT = 708;

export class Widget implements WidgetAPI {
  private _header: WidgetHeaderAPI = new Header();

  private _menu: WidgetMenuAPI = new Menu();

  private _notifications: WidgetNotificationsAPI;

  private _visibilityController: WidgetVisibilityAPI;

  private _onCloseHandler: undefined | (() => boolean | void);

  private _parentBus?: ParentMessageBus;

  private _actionHandler?: WidgetAPI['requestAction'];

  constructor({
    initialVisibility = 'closed',
    initialSoundSetting = 'disabled',
    parentBus,
    actionHandler,
  }: {
    initialVisibility?: Visibility;
    initialSoundSetting?: SoundSetting;
    parentBus?: ParentMessageBus;
    actionHandler?: WidgetAPI['requestAction'];
  } = {}) {
    this._parentBus = parentBus;
    if (parentBus) {
      this._visibilityController = new ParentVisibilityController({
        initialVisibility,
        parentBus,
      });
      this._notifications = new ParentNotifications({
        initialSoundSetting,
        parentBus,
      });
    } else {
      this._visibilityController = new DefaultVisibilityController(
        initialVisibility,
      );
      this._notifications = new DefaultNotifications(initialSoundSetting);
    }

    this._actionHandler = actionHandler;
  }

  get header(): WidgetHeaderAPI {
    return this._header;
  }

  get menu(): WidgetMenuAPI {
    return this._menu;
  }

  get notifications(): WidgetNotificationsAPI {
    return this._notifications;
  }

  get visibilityController(): WidgetVisibilityAPI {
    return this._visibilityController;
  }

  get parentBus(): ParentMessageBus | undefined {
    return this._parentBus;
  }

  visibility = (): Visibility => {
    return this.visibilityController.visibility();
  };

  visibilityObservable = (): Observable<Visibility> => {
    return this.visibilityController.visibilityObservable();
  };

  triggerParams = (): TriggerParams | null => {
    return this.visibilityController.triggerParams();
  };

  triggerParamsObservable = (): Observable<TriggerParams> => {
    return this.visibilityController.triggerParamsObservable();
  };

  setTriggerParams(triggerParams: TriggerParams | null) {
    this.visibilityController.setTriggerParams(triggerParams);
  }

  open = (): void => {
    this.visibilityController.open();
  };

  setOnClose = (handler?: (() => boolean | void) | undefined) => {
    this._onCloseHandler = handler;
  };

  // if the caller has set a close handler, we call it
  // first. if the handler returns true, signalling that it
  // handled the close call, we return and don't close the widget.
  // if the handler returns false, then we continue closing the widget.
  // to skip the handler, call close(true)
  //
  // examples:
  // passing a handler that returns true:
  // to show a close confirmation
  // passing a handler that returns false:
  // doing some cleanup on close (like ending a conversation)
  // passing close(skipHandler = true):
  // confirming close in the close confirmation
  close = (skipHandler = false): void => {
    if (!skipHandler && this._onCloseHandler) {
      const closeHandled = this._onCloseHandler();
      if (closeHandled) {
        return;
      }
    }

    this.visibilityController.close();
  };

  minimize = (): void => {
    this.visibilityController.minimize();
  };

  toggle = (): void => {
    this.visibilityController.toggle();
  };

  reset = (): void => {
    this.header.setTitle(undefined);
    this.menu.setMenuItems([]);
    this.setOnClose(undefined);
    this.notifications.setSoundSetting('disabled');
  };

  requestAnalyticsEventId = async (): Promise<string | null> => {
    if (this._parentBus) {
      return requestAnalyticsEventId(this._parentBus);
    }
    return Promise.resolve(null);
  };

  setPreferredSize = (size: {width: number; height: number}): void => {
    if (this._parentBus) {
      sendPreferredSize(this._parentBus, size);
    }
  };

  requestViewMode = (viewMode: ViewMode): void => {
    if (this._parentBus) {
      requestViewMode(this._parentBus, viewMode);
    }
  };

  setMinimizedHeight = (height: number): void => {
    if (this._parentBus) {
      sendMinimizedHeight(this._parentBus, height);
    }
  };

  requestAction: WidgetAPI['requestAction'] = (action, config) => {
    if (this._actionHandler) {
      return this._actionHandler(action, config);
    } else {
      return Promise.resolve({code: 500});
    }
  };
}
